/**
 * Mobile Navigation Component
 * Mobile overlay navigation for AccountsGenie dashboard
 * Following PRD Phase 2 responsive design specifications
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  MessageSquare,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  X,
  Wifi,
  WifiOff,
  Loader2,
  User
} from 'lucide-react';

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: React.ElementType;
  phase: number;
  requiresConnection?: boolean;
  badge?: string | number;
  disabled?: boolean;
}

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    phase: 2
  },
  {
    id: 'chat',
    label: 'AI Chat',
    path: '/chat',
    icon: MessageSquare,
    phase: 3,
    requiresConnection: true,
    disabled: true
  },
  {
    id: 'audit',
    label: 'Audit Log',
    path: '/audit',
    icon: FileText,
    phase: 4,
    disabled: true
  },
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: BarChart3,
    phase: 5,
    requiresConnection: true,
    disabled: true
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: Settings,
    phase: 6,
    disabled: true
  }
];

const MobileNav: React.FC<MobileNavProps> = ({
  isOpen,
  onClose,
  currentPath
}) => {
  const { user, logout } = useAuthStore();
  const { status: connectionStatus, checkConnection } = useConnectionStore();

  // Check Tally connection on mount
  useEffect(() => {
    checkConnection(false).catch(err =>
      console.error('[MobileNav] Connection check failed:', err)
    );
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Wifi className="h-4 w-4 text-green-500" />;
      case 'connecting':
        return <Loader2 className="h-4 w-4 text-yellow-500 animate-spin" />;
      default:
        return <WifiOff className="h-4 w-4 text-red-500" />;
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Tally';
      case 'connecting':
        return 'Connecting...';
      default:
        return 'Tally Offline';
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'connecting':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const isItemAccessible = (item: NavigationItem) => {
    if (item.disabled) return false;
    if (item.requiresConnection && connectionStatus !== 'connected') return false;
    return true;
  };

  const isCurrentPath = (path: string) => {
    return currentPath === path || (path === '/dashboard' && currentPath === '/');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Mobile Navigation Panel */}
      <div className="fixed left-0 top-0 h-full w-80 max-w-[85vw] bg-background border-r shadow-lg">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">AI</span>
              </div>
              <span className="font-semibold text-lg">AccountsGenie</span>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* User Profile */}
          <div className="p-4 border-b">
            <div className="flex items-center space-x-3">
              <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center">
                <User className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-medium truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-sm text-muted-foreground truncate">
                  {user?.email || 'user@example.com'}
                </p>
              </div>
            </div>
          </div>

          {/* Connection Status */}
          <div className="p-4">
            <Card className={cn(
              "border transition-colors",
              getConnectionStatusColor()
            )}>
              <CardContent className="p-3">
                <div className="flex items-center space-x-3">
                  {getConnectionStatusIcon()}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      Tally Connection
                    </p>
                    <p className="text-xs opacity-75">
                      {getConnectionStatusText()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const isActive = isCurrentPath(item.path);
              const isAccessible = isItemAccessible(item);
              const IconComponent = item.icon;

              return (
                <div key={item.id}>
                  {isAccessible ? (
                    <Link
                      to={item.path}
                      onClick={onClose}
                      className={cn(
                        "flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        isActive
                          ? "bg-accent text-accent-foreground"
                          : "text-muted-foreground"
                      )}
                    >
                      <IconComponent className="h-5 w-5 flex-shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge && (
                        <Badge variant="secondary" className="text-xs">
                          {item.badge}
                        </Badge>
                      )}
                    </Link>
                  ) : (
                    <div
                      className={cn(
                        "flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium",
                        "text-muted-foreground/50 cursor-not-allowed"
                      )}
                    >
                      <IconComponent className="h-5 w-5 flex-shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      <Badge variant="outline" className="text-xs">
                        Phase {item.phase}
                      </Badge>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer Actions */}
          <div className="p-4 border-t space-y-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                // Navigate to help page when implemented
                onClose();
              }}
              disabled
            >
              Help & Support
            </Button>
            
            <Button
              variant="outline"
              className="w-full text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileNav;